import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';

import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Client } from 'src/providers/client/client';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { ListingOptimizationOnboardingComponent } from 'src/shared/components/onboarding/listing-optimization-onboarding/listing-optimization-onboarding.component';

@Component({
  selector: 'listing-optimization-select',
  templateUrl: './listing-optimization-select.component.html',
  styleUrls: ['./listing-optimization-select.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ListingOptimizationSelectComponent implements OnInit {
  private readonly client = inject(Client);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly fb = inject(FormBuilder);

  readonly windowService = inject(WindowService);

  loaded = false;
  listingItems: { value: string; viewValue: string }[] = [];
  private listingsById: Record<string, any> = {};

  readonly form: FormGroup = this.fb.group({
    listingId: [''],
  });

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Listing Optimization');
    try {
      const addresses = await this.client.getAddresses();
      const allAddresses = addresses || [];

      const listingsPerAddress = await Promise.all(
        allAddresses.map(async (address: any) => {
          try {
            const listings = await this.client.getAddressListings(address.id);
            return (listings || [])
              .filter((l: any) => l.platform === 'airbnb')
              .map((listing: any) => ({ ...listing, address }));
          } catch (_err) {
            return [];
          }
        })
      );
      const airbnbListings = listingsPerAddress.flat();

      this.listingsById = Object.fromEntries(airbnbListings.map(listing => [String(listing.id), listing]));
      this.listingItems = airbnbListings.map(listing => ({
        value: String(listing.id),
        viewValue: this.listingLabel(listing),
      }));
      if (this.listingItems.length > 0) {
        this.form.patchValue({ listingId: this.listingItems[0].value }, { emitEvent: false });
      }
    } catch (_err) { /* intentionally empty */ }

    this.loaded = true;
    this.cdr.markForCheck();
  }

  private listingLabel(listing: any): string {
    const addressName = listing.address?.address_name
      || listing.address?.display_name
      || listing.address?.address1
      || `Property #${listing.address_id}`;
    return listing.platform_id ? `${addressName} — Airbnb ${listing.platform_id}` : `${addressName} — Airbnb`;
  }

  generateReport = async () => {
    const listing = this.listingsById[this.form.value.listingId];
    if (!listing) {return;}

    await this.storage.save('dialog-params', { fromMarketing: true, addressId: listing.address_id });

    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Optimization Report');
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.navigateTo('getting-started/listing-optimization', {}, ListingOptimizationOnboardingComponent);
    } else {
      this.navCtrl.navigateForward('getting-started/listing-optimization');
    }
  };
}
